import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  api,
  type AnordnungPaket,
  type Auflage,
  type LonLat,
  type Massnahme,
  type Nutzer,
  type Sperrstelle,
  type Veranstaltung,
} from '../api/client';
import { Schritte } from '../components/Schritte';
import { Feld, Karte, Meldung, StatusPill, fehlerText } from '../components/ui';
import { MASSNAHME_TEXT, ausLokal, datumZeit, uhrzeit, zuLokal } from '../format';
import { MapView } from '../map/MapView';
import type { KartenPunkt } from '../map/punkteLayer';
import { link } from '../router';
import * as umkehrbar from '../aktionen';
import { useUndo, type Schritt } from '../undo';

const STATUS_TEXT = {
  entwurf: 'Erfasst – Freigabe offen',
  korrektur: 'In Korrektur – neue Freigabe nötig',
  freigegeben: 'Freigegeben',
};

/** Skizze 2: Anordnung übernehmen – links Original-PDF, rechts Sperrstellen und Auflagen, unten Vier-Augen-Freigabe. */
export function Vorgaben({ id, nutzer }: { id: string; nutzer: Nutzer }) {
  const darf = nutzer.rolle === 'planer';
  const [v, setV] = useState<Veranstaltung | null>(null);
  const [paket, setPaket] = useState<AnordnungPaket | null>(null);
  const [route, setRoute] = useState<LonLat[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bearbeite, setBearbeite] = useState<Sperrstelle | 'neu' | null>(null);
  const [punkt, setPunkt] = useState<LonLat | null>(null);
  const [ausgewaehlt, setAusgewaehlt] = useState<string | null>(null);
  const [pdfVersion, setPdfVersion] = useState(0);

  const undo = useUndo();
  const stand = undo.stand;
  const laden = useCallback(async () => {
    try {
      const { veranstaltung } = await api.veranstaltung(id);
      setV(veranstaltung);
      setPaket(await api.anordnung(id));
      if (veranstaltung.planversion_id) {
        const r = await api.route(veranstaltung.planversion_id);
        setRoute(r.route?.geometry.coordinates ?? null);
      }
    } catch (e) {
      setFehler(fehlerText(e));
    }
  }, [id]);
  // stand: nach Rückgängig/Wiederholen neu laden
  useEffect(() => void laden(), [laden, stand]);

  const a = paket?.anordnung ?? null;
  const offen = darf && a !== null && a.status !== 'freigegeben';

  /** Umkehrbare Änderung (Rückgängig/Wiederholen). */
  async function schritt(s: Schritt) {
    setFehler(null);
    try {
      await undo.tue(s);
      setPaket(await api.anordnung(id));
      return true;
    } catch (e) {
      setFehler(fehlerText(e));
      return false;
    }
  }

  async function aktion(f: () => Promise<AnordnungPaket>) {
    setFehler(null);
    try {
      setPaket(await f());
      return true;
    } catch (e) {
      setFehler(fehlerText(e));
      return false;
    }
  }

  const punkte = useMemo<KartenPunkt[]>(() => {
    const p: KartenPunkt[] = (paket?.sperrstellen ?? []).map((s) => ({
      id: s.id,
      position: s.position.coordinates,
      status: s.massnahme === 'polizei' ? 'polizei' : 'neutral',
      label: s.kennung ?? '?',
      vorgabe: true,
    }));
    if (punkt) p.push({ id: 'neu', position: punkt, status: 'gelb', label: '+' });
    return p;
  }, [paket, punkt]);

  if (!paket) {
    return (
      <div className="seite">
        <Schritte id={id} aktiv="vorgaben" titel={v?.titel} />
        {fehler ? <Meldung art="fehler">{fehler}</Meldung> : <p>Lädt …</p>}
      </div>
    );
  }

  return (
    <div className="seite">
      <Schritte id={id} aktiv="vorgaben" titel={v?.titel} />
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}

      {!a ? (
        <AnordnungAnlegen
          darf={darf}
          onAnlegen={(d) => void aktion(() => api.anordnungAnlegen(id, d))}
        />
      ) : (
        <>
          <div className={`vorgabe-kopf vorgabe-kopf--${a.status}`}>
            <span aria-hidden="true">{a.status === 'freigegeben' ? '🔒' : '✎'}</span>
            <strong>Vorgabe-Ebene: {STATUS_TEXT[a.status]}</strong>
            {a.status === 'freigegeben' && (
              <span>
                von {a.freigegeben_von?.name} am {datumZeit(a.freigegeben_am)} – schreibgeschützt,
                Änderungen nur über „Vorgabe korrigieren“.
              </span>
            )}
          </div>

          <div className="vorgaben">
            <div className="vorgaben__pdf">
              <Karte titel="Original-Anordnung (PDF)">
                {a.pdf ? (
                  <iframe
                    className="pdf"
                    title="Anordnung als PDF"
                    src={`${api.pdfUrl(a.id)}?v=${pdfVersion}`}
                  />
                ) : (
                  <p className="gedaempft">Noch kein PDF hochgeladen.</p>
                )}
                {offen && (
                  <Feld
                    label={a.pdf ? 'PDF ersetzen' : 'PDF hochladen'}
                    hinweis="Original der Anordnung bzw. des Verkehrszeichenplans, höchstens 20 MB."
                  >
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f)
                          void aktion(() => api.pdfHochladen(a.id, f)).then(() =>
                            setPdfVersion((x) => x + 1),
                          );
                      }}
                    />
                  </Feld>
                )}
              </Karte>
              <Metadaten
                paket={paket}
                offen={offen}
                onSpeichern={(d) => void schritt(umkehrbar.anordnungAendern(a, d))}
              />
            </div>

            <div className="vorgaben__daten">
              <Karte
                titel={`Angeordnete Sperrstellen (${paket.sperrstellen.length})`}
                aktion={
                  offen && (
                    <button
                      type="button"
                      className="knopf knopf--primaer"
                      onClick={() => {
                        setBearbeite('neu');
                        setPunkt(null);
                      }}
                    >
                      Sperrstelle erfassen
                    </button>
                  )
                }
              >
                <div className="vorgaben__karte">
                  <MapView
                    route={route}
                    punkte={punkte}
                    ausgewaehltId={ausgewaehlt}
                    punktWaehlen={bearbeite !== null}
                    onKartenKlick={setPunkt}
                    onAuswahl={setAusgewaehlt}
                  />
                </div>
                {bearbeite !== null && (
                  <SperrstelleFormular
                    vorlage={bearbeite === 'neu' ? null : bearbeite}
                    punkt={punkt}
                    veranstaltung={v}
                    onAbbrechen={() => {
                      setBearbeite(null);
                      setPunkt(null);
                    }}
                    onSpeichern={async (d) => {
                      const ok = await schritt(
                        bearbeite === 'neu'
                          ? umkehrbar.sperrstelleAnlegen(a.id, d)
                          : umkehrbar.sperrstelleAendern(bearbeite, d),
                      );
                      if (ok) {
                        setBearbeite(null);
                        setPunkt(null);
                      }
                    }}
                  />
                )}
                <ul className="vorgabe-liste">
                  {paket.sperrstellen.map((s) => (
                    <li
                      key={s.id}
                      className={`vorgabe-eintrag${ausgewaehlt === s.id ? ' vorgabe-eintrag--aktiv' : ''}`}
                    >
                      <button type="button" className="link" onClick={() => setAusgewaehlt(s.id)}>
                        <strong>🔒 {s.kennung ?? '–'}</strong> {s.bezeichnung}
                      </button>
                      <div>
                        {s.massnahme ? MASSNAHME_TEXT[s.massnahme] : ''}
                        {s.sperrzeit_von &&
                          ` · ${uhrzeit(s.sperrzeit_von)}–${uhrzeit(s.sperrzeit_bis)} Uhr`}
                        {s.aufheben_nach_durchzug && ' · nach Durchzug aufheben'}
                      </div>
                      <div className="gedaempft">
                        Fundstelle: {s.fundstelle ?? 'fehlt'} · Quelle:{' '}
                        {s.quelle === 'ki' ? 'KI-Import' : 'manuell'}
                      </div>
                      {offen && (
                        <div className="knopfreihe">
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            onClick={() => {
                              setBearbeite(s);
                              setPunkt(s.position.coordinates);
                            }}
                          >
                            Korrigieren
                          </button>
                          <button
                            type="button"
                            className="knopf knopf--klein knopf--gefahr"
                            onClick={() =>
                              window.confirm(`Sperrstelle ${s.kennung ?? ''} löschen?`) &&
                              void (async () => {
                                const pv = v?.planversion_id;
                                const posten = pv ? (await api.posten(pv)).posten : [];
                                await schritt(umkehrbar.sperrstelleLoeschen(a.id, s, posten));
                              })()
                            }
                          >
                            Löschen
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </Karte>

              <Auflagen paket={paket} darf={darf} offen={offen} vid={id} schritt={schritt} />
            </div>
          </div>

          <Freigabe paket={paket} nutzer={nutzer} aktion={aktion} />
          <div className="knopfreihe">
            <a className="knopf knopf--primaer" href={link('v', id, 'planung')}>
              Weiter: Route & Abgleich
            </a>
          </div>
        </>
      )}
    </div>
  );
}

function AnordnungAnlegen({
  darf,
  onAnlegen,
}: {
  darf: boolean;
  onAnlegen: (d: {
    behoerde: string | null;
    aktenzeichen: string | null;
    datum: string | null;
  }) => void;
}) {
  const [behoerde, setBehoerde] = useState('');
  const [az, setAz] = useState('');
  const [datum, setDatum] = useState('');
  return (
    <Karte titel="Anordnung erfassen">
      <p>
        Grundlage der Planung ist die verkehrsbehördliche Anordnung. Erfassen Sie deren Angaben –
        Sperrstellen, Sperrzeiten und Auflagen jeweils mit Fundstelle. Eine zweite Person gibt die
        Vorgabe anschließend frei.
      </p>
      {darf ? (
        <form
          className="formular"
          onSubmit={(e) => {
            e.preventDefault();
            onAnlegen({
              behoerde: behoerde || null,
              aktenzeichen: az || null,
              datum: datum || null,
            });
          }}
        >
          <Feld label="Behörde">
            <input value={behoerde} onChange={(e) => setBehoerde(e.target.value)} />
          </Feld>
          <Feld label="Aktenzeichen">
            <input value={az} onChange={(e) => setAz(e.target.value)} />
          </Feld>
          <Feld label="Datum der Anordnung">
            <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
          </Feld>
          <button type="submit" className="knopf knopf--primaer">
            Anordnung anlegen
          </button>
        </form>
      ) : (
        <p className="gedaempft">Noch keine Anordnung erfasst.</p>
      )}
    </Karte>
  );
}

function Metadaten({
  paket,
  offen,
  onSpeichern,
}: {
  paket: AnordnungPaket;
  offen: boolean;
  onSpeichern: (d: {
    behoerde: string | null;
    aktenzeichen: string | null;
    datum: string | null;
  }) => void;
}) {
  const a = paket.anordnung!;
  const [behoerde, setBehoerde] = useState(a.behoerde ?? '');
  const [az, setAz] = useState(a.aktenzeichen ?? '');
  const [datum, setDatum] = useState(a.datum ?? '');
  return (
    <Karte titel="Angaben zur Anordnung">
      <fieldset className="formular" disabled={!offen}>
        <Feld label="Behörde">
          <input value={behoerde} onChange={(e) => setBehoerde(e.target.value)} />
        </Feld>
        <Feld label="Aktenzeichen">
          <input value={az} onChange={(e) => setAz(e.target.value)} />
        </Feld>
        <Feld label="Datum">
          <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
        </Feld>
        {offen && (
          <button
            type="button"
            className="knopf"
            onClick={() =>
              onSpeichern({
                behoerde: behoerde || null,
                aktenzeichen: az || null,
                datum: datum || null,
              })
            }
          >
            Speichern
          </button>
        )}
      </fieldset>
    </Karte>
  );
}

function SperrstelleFormular({
  vorlage,
  punkt,
  veranstaltung,
  onAbbrechen,
  onSpeichern,
}: {
  vorlage: Sperrstelle | null;
  punkt: LonLat | null;
  veranstaltung: Veranstaltung | null;
  onAbbrechen: () => void;
  onSpeichern: (d: Parameters<typeof api.sperrstelleAnlegen>[1]) => Promise<void>;
}) {
  const tag = veranstaltung ? zuLokal(veranstaltung.startzeit).slice(0, 11) : '';
  const [kennung, setKennung] = useState(vorlage?.kennung ?? '');
  const [bezeichnung, setBezeichnung] = useState(vorlage?.bezeichnung ?? '');
  const [massnahme, setMassnahme] = useState<Massnahme>(vorlage?.massnahme ?? 'vollsperrung');
  const [von, setVon] = useState(vorlage ? zuLokal(vorlage.sperrzeit_von) : '');
  const [bis, setBis] = useState(vorlage ? zuLokal(vorlage.sperrzeit_bis) : '');
  const [aufheben, setAufheben] = useState(vorlage?.aufheben_nach_durchzug ?? true);
  const [fundstelle, setFundstelle] = useState(vorlage?.fundstelle ?? '');

  function senden(e: FormEvent) {
    e.preventDefault();
    if (!punkt) return;
    void onSpeichern({
      kennung: kennung || null,
      bezeichnung: bezeichnung || null,
      massnahme,
      sperrzeit_von: ausLokal(von),
      sperrzeit_bis: ausLokal(bis),
      aufheben_nach_durchzug: aufheben,
      fundstelle,
      position: { type: 'Point', coordinates: punkt },
    });
  }

  return (
    <form className="formular formular--rahmen" onSubmit={senden}>
      <h3>{vorlage ? `Sperrstelle ${vorlage.kennung ?? ''} korrigieren` : 'Neue Sperrstelle'}</h3>
      <p className={punkt ? 'gedaempft' : 'hinweis-stark'}>
        {punkt
          ? `Lage: ${punkt[1].toFixed(5)}, ${punkt[0].toFixed(5)} – zum Ändern erneut auf die Karte tippen.`
          : 'Lage: bitte auf der Karte antippen.'}
      </p>
      <div className="formular__raster">
        <Feld label="Nr. laut Anordnung">
          <input
            value={kennung}
            onChange={(e) => setKennung(e.target.value)}
            maxLength={20}
            placeholder="z. B. S4"
          />
        </Feld>
        <Feld label="Bezeichnung">
          <input
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.target.value)}
            placeholder="Straße / Kreuzung"
          />
        </Feld>
        <Feld label="Maßnahme">
          <select value={massnahme} onChange={(e) => setMassnahme(e.target.value as Massnahme)}>
            {Object.entries(MASSNAHME_TEXT).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </select>
        </Feld>
        <Feld
          label="Fundstelle (Pflicht)"
          hinweis="Seite/Nr. in Anordnung oder Verkehrszeichenplan"
        >
          <input
            value={fundstelle}
            onChange={(e) => setFundstelle(e.target.value)}
            required
            placeholder="S. 2, Nr. 4"
          />
        </Feld>
        <Feld label="Sperrzeit von">
          <input
            type="datetime-local"
            value={von || ''}
            placeholder={tag}
            onChange={(e) => setVon(e.target.value)}
          />
        </Feld>
        <Feld label="Sperrzeit bis">
          <input type="datetime-local" value={bis || ''} onChange={(e) => setBis(e.target.value)} />
        </Feld>
      </div>
      <label className="check">
        <input type="checkbox" checked={aufheben} onChange={(e) => setAufheben(e.target.checked)} />{' '}
        Sperrung nach Durchzug aufheben
      </label>
      <div className="knopfreihe">
        <button type="submit" className="knopf knopf--primaer" disabled={!punkt}>
          Speichern
        </button>
        <button type="button" className="knopf" onClick={onAbbrechen}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function Auflagen({
  paket,
  darf,
  offen,
  vid,
  schritt,
}: {
  paket: AnordnungPaket;
  darf: boolean;
  offen: boolean;
  vid: string;
  schritt: (s: Schritt) => Promise<boolean>;
}) {
  const a = paket.anordnung!;
  const [text, setText] = useState('');
  const [fundstelle, setFundstelle] = useState('');
  const [regel, setRegel] = useState('');
  const [ende, setEnde] = useState('');

  return (
    <Karte titel={`Auflagen (${paket.auflagen.length})`}>
      <ul className="vorgabe-liste">
        {paket.auflagen.map((x: Auflage) => (
          <li key={x.id} className="vorgabe-eintrag">
            <label className="check">
              <input
                type="checkbox"
                checked={x.erledigt}
                disabled={!darf}
                onChange={(e) => void schritt(umkehrbar.auflageErledigt(x, e.target.checked))}
              />
              <span>
                {x.text} {x.fundstelle && <span className="gedaempft">({x.fundstelle})</span>}
              </span>
            </label>
            <div className="gedaempft">
              {x.pruefart === 'regel'
                ? `Prüfregel: ${x.regel_key === 'ENDE_SPAETESTENS' ? `Ende spätestens ${uhrzeit(String(x.parameter?.zeit ?? ''))}` : x.regel_key}`
                : 'manuell abhaken'}
              {x.verantwortlich && ` · verantwortlich: ${x.verantwortlich}`}
            </div>
            {offen && (
              <button
                type="button"
                className="knopf knopf--klein knopf--gefahr"
                onClick={() => void schritt(umkehrbar.auflageLoeschen(a.id, x, paket.auflagen))}
              >
                Löschen
              </button>
            )}
          </li>
        ))}
      </ul>
      {offen && (
        <form
          className="formular formular--rahmen"
          onSubmit={(e) => {
            e.preventDefault();
            void schritt(
              umkehrbar.auflageAnlegen(vid, a.id, {
                text,
                fundstelle: fundstelle || null,
                pruefart: regel ? 'regel' : 'manuell',
                regel_key: regel || null,
                ende_spaetestens: regel === 'ENDE_SPAETESTENS' ? ausLokal(ende) : null,
              }),
            ).then((ok) => ok && (setText(''), setFundstelle('')));
          }}
        >
          <h3>Auflage hinzufügen</h3>
          <Feld label="Text der Auflage">
            <input value={text} onChange={(e) => setText(e.target.value)} required />
          </Feld>
          <Feld label="Fundstelle">
            <input
              value={fundstelle}
              onChange={(e) => setFundstelle(e.target.value)}
              placeholder="S. 3, Nr. 1"
            />
          </Feld>
          <Feld label="Prüfung">
            <select value={regel} onChange={(e) => setRegel(e.target.value)}>
              <option value="">manuell abhaken</option>
              <option value="ENDE_SPAETESTENS">automatisch: Ende spätestens …</option>
              <option value="RETTUNGSWEG_FREI">
                Rettungsweg freihalten (Prüfung folgt, bis dahin manuell)
              </option>
            </select>
          </Feld>
          {regel === 'ENDE_SPAETESTENS' && (
            <Feld label="Ende spätestens">
              <input
                type="datetime-local"
                value={ende}
                onChange={(e) => setEnde(e.target.value)}
                required
              />
            </Feld>
          )}
          <button type="submit" className="knopf">
            Hinzufügen
          </button>
        </form>
      )}
    </Karte>
  );
}

function Freigabe({
  paket,
  nutzer,
  aktion,
}: {
  paket: AnordnungPaket;
  nutzer: Nutzer;
  aktion: (f: () => Promise<AnordnungPaket>) => Promise<boolean>;
}) {
  const a = paket.anordnung!;
  const darf = nutzer.rolle === 'planer';
  const selbstErfasst = a.erfasst_von?.id === nutzer.id;
  const [grund, setGrund] = useState('');
  const [fundstelle, setFundstelle] = useState('');

  return (
    <Karte titel="Vier-Augen-Freigabe">
      <p>
        Erfasst {a.status === 'korrektur' ? 'bzw. korrigiert ' : ''}von{' '}
        <strong>{a.erfasst_von?.name ?? 'unbekannt'}</strong>.{' '}
        {a.status === 'freigegeben' ? (
          <>
            Freigegeben von <strong>{a.freigegeben_von?.name}</strong> am{' '}
            {datumZeit(a.freigegeben_am)}.
          </>
        ) : (
          'Die Freigabe muss eine andere Person vornehmen.'
        )}
      </p>
      {a.korrektur && (
        <p className="gedaempft">
          Letzte Korrektur: {a.korrektur.grund} (Fundstelle {a.korrektur.fundstelle},{' '}
          {datumZeit(a.korrektur.am)})
        </p>
      )}
      {darf && a.status !== 'freigegeben' && (
        <>
          <p>
            <StatusPill
              status="gelb"
              text="Prüfen Sie jede Angabe gegen das Original, bevor Sie freigeben."
            />
          </p>
          <button
            type="button"
            className="knopf knopf--primaer"
            disabled={selbstErfasst || paket.sperrstellen.length === 0}
            onClick={() => void aktion(() => api.freigeben(a.id))}
          >
            Vorgabe freigeben
          </button>
          {selbstErfasst && (
            <p className="gedaempft">
              Sie haben diese Fassung erfasst und können sie daher nicht selbst freigeben.
            </p>
          )}
        </>
      )}
      {darf && a.status === 'freigegeben' && (
        <form
          className="formular formular--rahmen"
          onSubmit={(e) => {
            e.preventDefault();
            void aktion(() => api.korrigieren(a.id, grund, fundstelle));
          }}
        >
          <h3>Vorgabe korrigieren</h3>
          <p className="gedaempft">
            Nur bei einer Änderung durch die Behörde oder einem Erfassungsfehler. Danach ist eine
            neue Vier-Augen-Freigabe nötig.
          </p>
          <Feld label="Grund">
            <input value={grund} onChange={(e) => setGrund(e.target.value)} required />
          </Feld>
          <Feld label="Fundstelle (z. B. Nachtrag der Behörde)">
            <input value={fundstelle} onChange={(e) => setFundstelle(e.target.value)} required />
          </Feld>
          <button type="submit" className="knopf">
            Korrektur beginnen
          </button>
        </form>
      )}
      {paket.protokoll.length > 0 && (
        <details>
          <summary>Protokoll ({paket.protokoll.length})</summary>
          <ul className="einfache-liste">
            {paket.protokoll.map((p, i) => (
              <li key={i}>
                {datumZeit(p.am)} – {p.aktion} {p.von && `(${p.von})`} {p.text && `– ${p.text}`}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Karte>
  );
}
